import { useCallback, useEffect, useRef, useState } from "react";

import { buscarSettings } from "../../services/settingsEmVoo";
import { getPublicFileUrl } from "../../config";
import usePrefTickets from "../usePrefTickets";
import padrao from "../../assets/sound.mp3";

// O som que avisa mensagem nova.
//
// Duas decisões, e elas são de donos diferentes:
//
// - QUAL som é da EMPRESA (configuração, chave `notificationSound`): o aviso
//   faz parte de como a operação soa, e todo mundo na sala ouve o mesmo.
// - QUANTO alto é de QUEM OUVE: o volume da empresa é o padrão, e cada pessoa
//   sobe ou baixa o dela. Som alto no aberto de uma sala incomoda quem está ao
//   lado, e o fone de cada um é diferente.
//
// Antes disto o arquivo era fixo no código e o volume era o do sistema — não
// havia nem escolha nem ajuste.

export const SONS_PRONTOS = [
	{ valor: "padrao", rotulo: "Padrão do sistema", url: padrao },
	{ valor: "sino", rotulo: "Sino (dois tons)", url: "/sons/sino.wav" },
	{ valor: "toque", rotulo: "Toque curto", url: "/sons/toque.wav" },
	{ valor: "discreto", rotulo: "Discreto (grave)", url: "/sons/discreto.wav" },
	{ valor: "duplo", rotulo: "Duplo (chama mais)", url: "/sons/duplo.wav" },
];

// "custom" não entra na lista acima: ele não tem URL fixa, vem do arquivo que
// a empresa enviou.
export const SOM_ENVIADO = "custom";

export const urlDoSom = (chave, arquivoEnviado) => {
	if (chave === SOM_ENVIADO) {
		return arquivoEnviado ? getPublicFileUrl(arquivoEnviado) : padrao;
	}

	return SONS_PRONTOS.find((s) => s.valor === chave)?.url || padrao;
};

// Volume 0–100 na tela, 0–1 no elemento de áudio.
export const VOLUME_PADRAO = 70;

const numeroOuPadrao = (valor, padraoDoCampo) => {
	// Ausente primeiro, e não `Number()` direto: `Number(null)` e `Number("")`
	// dão ZERO, que é um volume válido — então "nunca mexi no meu volume"
	// (`null`) e configuração sem linha no banco (`""`) viravam mudo, e o aviso
	// não tocava para ninguém que não tivesse arrastado a barra.
	if (valor === null || valor === undefined || valor === "") {
		return padraoDoCampo;
	}

	const n = Number(valor);
	return Number.isFinite(n) && n >= 0 && n <= 100 ? n : padraoDoCampo;
};

const useSomDeNotificacao = () => {
	const [config, setConfig] = useState({ som: "padrao", arquivo: "", volume: VOLUME_PADRAO });
	// Ajuste pessoal. `null` significa "não mexi" — e aí vale o da empresa, em
	// vez de um número gravado que envelheceria quando o admin mudasse o padrão.
	const [volumeDaPessoa] = usePrefTickets("volumeNotificacao", null);

	useEffect(() => {
		let vivo = true;

		buscarSettings()
			.then(({ data }) => {
				if (!vivo) return;

				const valor = (chave) =>
					(Array.isArray(data) ? data : []).find((s) => s.key === chave)?.value;

				setConfig({
					som: valor("notificationSound") || "padrao",
					arquivo: valor("notificationSoundFile") || "",
					volume: numeroOuPadrao(valor("notificationVolume"), VOLUME_PADRAO),
				});
			})
			.catch(() => {
				// Sem resposta, o aviso continua tocando com o som de sempre: ficar
				// mudo por causa de uma configuração é pior do que soar diferente.
			});

		return () => {
			vivo = false;
		};
	}, []);

	const volume = numeroOuPadrao(volumeDaPessoa, config.volume);

	// Um elemento só, reaproveitado: criar um `Audio` a cada mensagem deixa o
	// navegador com dezenas de players pendurados numa conversa movimentada.
	const audioRef = useRef(null);
	const url = urlDoSom(config.som, config.arquivo);

	useEffect(() => {
		if (!audioRef.current) audioRef.current = new Audio();
		audioRef.current.src = url;
		audioRef.current.volume = volume / 100;
	}, [url, volume]);

	const tocar = useCallback(() => {
		const player = audioRef.current;
		if (!player || volume <= 0) return;

		// Volta ao começo: duas mensagens seguidas devem soar duas vezes, e não
		// uma só porque a primeira ainda estava tocando.
		player.currentTime = 0;
		// O navegador recusa som antes da primeira interação da pessoa com a
		// página. Não é erro nosso e não pode virar exceção no console.
		player.play().catch(() => undefined);
	}, [volume]);

	return { tocar, volume, som: config.som, arquivoEnviado: config.arquivo };
};

export default useSomDeNotificacao;
